import React from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Building2,
  Store,
  GraduationCap,
  School,
  TrafficCone,
} from "lucide-react";

const verticals = [
  {
    Icon: Building2,
    tag: "Corporate Campuses",
    blurb: "Subsidised meals, employee wallets, HRMS-grade reporting.",
    testid: "grubpe-vertical-corporate",
  },
  {
    Icon: Store,
    tag: "Malls & Food Courts",
    blurb: "Unified kiosk + app + POS · multi-vendor carts · weekly settlement.",
    testid: "grubpe-vertical-malls",
  },
  {
    Icon: GraduationCap,
    tag: "Colleges & Universities",
    blurb: "Hostel mess + day canteen on one wallet, one ID, one ledger.",
    testid: "grubpe-vertical-universities",
  },
  {
    Icon: School,
    tag: "K-12 Schools",
    blurb: "RFID tap-to-pay, allergen controls, parent oversight.",
    testid: "grubpe-vertical-schools",
  },
  {
    Icon: TrafficCone,
    tag: "Expressway Food Courts",
    blurb: "Offline-resilient POS · 60-sec ticket · vendor payouts.",
    testid: "grubpe-vertical-expressways",
  },
];

const stats = [
  { v: "50+", k: "Food court locations" },
  { v: "200+", k: "Vendor menus" },
  { v: "27K+", k: "Active SKUs" },
  { v: "3.9M+", k: "Orders processed 2025" },
  { v: "99.9%", k: "Uptime SLA" },
];

const ACCENT = "#0038FF";

const Grubpe = () => {
  return (
    <section
      id="grubpe"
      data-testid="grubpe-section"
      className="py-24 lg:py-32 bg-white border-y border-[#e5e5e5] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-12 gap-x-3 gap-y-8 lg:gap-12 mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#0038FF]">[ 05 ]</span>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0038FF]">
                Food-tech Product
              </span>
            </div>

            <a
              href="https://grubpe.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="grubpe-external-badge"
              className="group inline-flex items-center gap-2 bg-[#0038FF] text-white px-4 py-2 mb-8 transition-opacity hover:opacity-90"
            >
              <span className="text-xs uppercase tracking-[0.2em] font-bold">
                Grubpe
              </span>
              <ArrowUpRight
                className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.25}
              />
            </a>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="grubpe-heading"
              className="font-display text-[2.4rem] sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[1.05] text-[#0a0a0a] mb-8 break-words"
            >
              The operating system
              <br />
              for{" "}
              <span className="italic font-medium text-[#0038FF]">
                multi-vendor dining.
              </span>
            </motion.h2>

            <p className="text-lg text-[#4a4a4a] leading-relaxed max-w-xl mb-10">
              Grubpe powers ordering, payments, settlements and analytics for
              corporate campuses, malls, universities, schools and expressway
              food courts — on one in-house stack we build, deploy and own
              end-to-end.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://grubpe.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="grubpe-visit-cta"
                className="btn-sharp-shadow inline-flex items-center justify-center gap-3 bg-[#0a0a0a] text-white px-5 sm:px-7 py-4 text-xs sm:text-sm font-semibold uppercase tracking-wider whitespace-nowrap"
              >
                Visit grubpe.com
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
              </a>
              <a
                href="#contact"
                data-testid="grubpe-demo-cta"
                className="inline-flex items-center justify-center gap-3 border-2 border-[#0a0a0a] text-[#0a0a0a] px-5 sm:px-7 py-4 text-xs sm:text-sm font-semibold uppercase tracking-wider hover:bg-[#0a0a0a] hover:text-white transition-colors duration-300 whitespace-nowrap"
              >
                Request a demo
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
              </a>
            </div>
          </div>

          {/* Stats column */}
          <div className="col-span-12 lg:col-span-5 lg:pl-10 flex flex-col justify-end">
            <div className="grid grid-cols-2 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
              {stats.map((s, idx) => (
                <div
                  key={s.k}
                  data-testid={`grubpe-stat-${idx}`}
                  className={`bg-white p-6 ${
                    idx === stats.length - 1 ? "col-span-2" : ""
                  }`}
                >
                  <div
                    className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-[#0a0a0a]"
                  >
                    {s.v}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#4a4a4a] mt-2">
                    {s.k}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Verticals heading */}
        <div className="border-t border-[#e5e5e5] pt-10 mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-xs text-[#0038FF]">/05</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0038FF]">
              One Stack · Five Verticals
            </span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0a0a0a] max-w-2xl">
            Same backend. Re-skinned and re-tuned for each place people eat.
          </h3>
        </div>

        {/* Verticals grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
          {verticals.map((v, idx) => {
            const Icon = v.Icon;
            return (
              <motion.div
                key={v.tag}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: idx * 0.06 }}
                data-testid={v.testid}
                className="group bg-white p-6 lg:p-7 min-h-[200px] flex flex-col justify-between hover:bg-[#0a0a0a] transition-colors duration-400"
              >
                <div
                  className="w-10 h-10 border border-[#0a0a0a]/15 group-hover:border-white/25 flex items-center justify-center"
                >
                  <Icon
                    className="w-5 h-5 text-[#0a0a0a] group-hover:text-white transition-colors"
                    strokeWidth={1.75}
                  />
                </div>
                <div>
                  <div
                    className="font-display text-lg font-bold tracking-tight text-[#0a0a0a] group-hover:text-white transition-colors mb-2 leading-tight"
                  >
                    {v.tag}
                  </div>
                  <p className="text-xs leading-relaxed text-[#4a4a4a] group-hover:text-white/70 transition-colors">
                    {v.blurb}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Grubpe;
